import React, { useState, useRef, useMemo } from 'react';
import { PageTitle } from '../../../../../_metronic/layout/core';
import { SpreadsheetData } from '../types/spreadsheet';
import { useDebounce } from '../hooks/useDebounce';
import { useSpreadsheetData } from '../hooks/useSpreadsheetData';
import SpreadsheetHeader from './SpreadsheetHeader';
import SpreadsheetControls from './SpreadsheetControls';
import SpreadsheetTable from './SpreadsheetTable';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import SavingIndicator from './SavingIndicator';

const Spreadsheet: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    columns,
    rows,
    columnFilters,
    activeFilters,
    sortState,
    isLoading,
    isSaving,
    error,
    loadDataFromJSON,
    loadDataFromAPI,
    updateCellValue,
    startEditing,
    stopEditing,
    addNewRow,
    insertNewRow,
    deleteRow,
    handleSort,
    handleFilterChange,
    clearFilter,
    clearAllFilters,
    getFilteredAndSortedRows
  } = useSpreadsheetData();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          loadDataFromJSON(data);
        } catch (error) {
          alert('Erro ao carregar arquivo JSON. Verifique o formato.');
        }
      };
      reader.readAsText(file);
    }
  };

  const exportData = () => {
    const exportData: SpreadsheetData = {
      DataTransfer: columns.map(columnName => ({
        columnName,
        data: rows.map((row:any) => row[columnName]?.value || '')
      }))
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'planilha_export.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredRows = useMemo(() => {
    return getFilteredAndSortedRows(debouncedSearchTerm);
  }, [getFilteredAndSortedRows, debouncedSearchTerm]);

  const isSearching = searchTerm !== debouncedSearchTerm;
  const activeFilterCount = Object.keys(activeFilters).length;
  const hasActiveFiltersOrSearch = debouncedSearchTerm.trim() !== '' || activeFilterCount > 0;

  // Mostrar estado de carregamento
  if (isLoading) {
    return <LoadingState />;
  }

  // Mostrar erro se houver
  if (error && columns.length === 0) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Erro ao carregar dados</h3>
            <p className="text-gray-600 mb-6">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <PageTitle>Descrição das Tabelas</PageTitle>
      {/* Indicador de salvamento */}
      <SavingIndicator isVisible={isSaving} />
      
      {/* Header fixo */}
      {/*<div className="sticky top-0 z-30 bg-gradient-to-br from-slate-50 to-blue-50 shadow-md">
        <div className="mx-auto px-6 pt-6">
          <SpreadsheetHeader
            onLoadSampleData={loadSampleData}
            onFileUpload={handleFileUpload}
            onExportData={exportData}
            onRefreshData={loadDataFromAPI}
            fileInputRef={fileInputRef}
            hasData={rows.length > 0}
            isLoading={isLoading}
          />
        </div>
      </div>*/}

      {/* Controles fixos */}
      {columns.length > 0 && (
        <div className="sticky z-20 ">
          <div className="mx-auto px-6">
            <SpreadsheetControls
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filteredCount={filteredRows.length}
              totalCount={rows.length}
              onAddNewRow={addNewRow}
              activeFilters={activeFilters}
              onClearFilter={clearFilter}
              onClearAllFilters={clearAllFilters}
              isSearching={isSearching}
            />
          </div>
        </div>
      )}

      {/* Área de conteúdo com scroll */}
      <div className="mx-auto px-6">
        <div className={`${columns.length > 0 ? 'max-h-[calc(100vh-280px)] overflow-y-auto' : ''}`}>
          {columns.length > 0 ? (
            filteredRows.length > 0 ? (
              <SpreadsheetTable
                columns={columns}
                filteredRows={filteredRows}
                originalRows={rows}
                columnFilters={columnFilters}
                activeFilters={activeFilters}
                sortState={sortState}
                onSort={handleSort}
                onFilterChange={handleFilterChange}
                onClearFilter={clearFilter}
                onStartEditing={startEditing}
                onStopEditing={stopEditing}
                onUpdateCell={updateCellValue}
                onDeleteRow={deleteRow}
                onInsertRow={insertNewRow}
                hasActiveFiltersOrSearch={hasActiveFiltersOrSearch}
              />
            ) : (
              <EmptyState
                type="no-results"
                searchTerm={debouncedSearchTerm}
                activeFilterCount={activeFilterCount}
                onClearAllFilters={clearAllFilters}
              />
            )
          ) : (
            <EmptyState
              type="no-data"
              
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Spreadsheet;